import type { DraftPlayerResponse } from '@on3/api';
import { formatMillions } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { On3LogoAlt } from 'components/Svg/On3LogoAlt';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';

import styles from './IndustryComparisonDraftPlayerItem.module.scss';

interface IndustryComparisonDraftPlayerItemProps extends DraftPlayerResponse {
  className?: string;
  selected?: boolean;
}
export const IndustryComparisonDraftPlayerItem = ({
  player,
  rating: ratingResponse,
  nilValue,
  organization,
  pick,
  round,
  overallPick,
  className,
  selected,
}: IndustryComparisonDraftPlayerItemProps) => {
  const {
    defaultAsset,
    fullName,
    hometown,
    highSchool,
    slug,
    position,
    height,
    weight,
    athleteVerified,
    state,
  } = player || {};
  const {
    positionAbbr,
    stateAbbr,
    fiveStarPlus,
    consensusRating,
    consensusStars,
    consensusNationalRank,
    consensusPositionRank,
    consensusStateRank,
    year,
    sport,
  } = ratingResponse || {};

  const formattedNilValue = nilValue ? (
    <span className={styles.nilValue}>${formatMillions(nilValue)}</span>
  ) : (
    <span className={styles.valuationUndisclosed}>undisclosed</span>
  );
  const formattedConsensusRating = consensusRating?.toFixed(2);

  return (
    <div className={clsx(styles.playerItemContainer, className)}>
      <div className={styles.industryComparisonDraftPickItem}>
        <Text
          className={styles.pickContainer}
          component="div"
          variant="subtitle1"
        >
          <span className={styles.overallPick}>{overallPick}</span>
          <span>RND {round}</span>
          <span>({pick})</span>
        </Text>
        <Text className={styles.pickTeam}>
          <TeamAvatar
            className={styles.pickTeamLogo}
            height={20}
            slug={organization?.slug}
            src={organization?.assetUrl}
            width={20}
          />

          <Text className={styles.proName} component="span">
            {organization?.abbreviation}
          </Text>
        </Text>
      </div>
      <On3Avatar
        altText={defaultAsset?.altText}
        className={clsx(styles.playerAvatar)}
        domain={defaultAsset?.domain}
        source={defaultAsset?.source}
      />

      <div className={styles.playerDetailsContainer}>
        <div className={styles.playerName}>
          <TextLink
            color="primary"
            href={`/db/${slug}/`}
            underline="none"
            variant="h5"
          >
            {fullName}
          </TextLink>
          {athleteVerified && <AthleteVerified />}
        </div>
        <Text className={styles.vitalsContainer}>
          <span>{position?.abbr ? position.abbr : '-'}</span>

          <span className={styles.height}>{height ?? '-'}</span>

          <span className={styles.weight}>{weight ? weight : '-'}</span>
          {formattedNilValue && (
            <span className={styles.nil}>
              <span className={styles.nilLabel}>NIL Value</span>
              {formattedNilValue}
            </span>
          )}
        </Text>

        <Text className={clsx(styles.hometownContainer, styles.mobile)}>
          <TextLink
            className={styles.highSchool}
            href={`/high-school/${highSchool?.urlSlug}/`}
          >
            {highSchool?.name}
          </TextLink>
          <span className={styles.homeTown}>
            {hometown?.name}, {state?.abbr}
          </span>
        </Text>
      </div>

      {consensusRating && (
        <Text
          className={clsx(styles.consensusRating, styles.mobile)}
          component="span"
        >
          {formattedConsensusRating}
        </Text>
      )}

      <div className={clsx(styles.rankingHeaderContainer, styles.mobileHeader)}>
        <Text
          aria-label="National Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          Natl
        </Text>
        <Text
          aria-label="Position Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          Pos
        </Text>
        <Text
          aria-label="State Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          St
        </Text>
      </div>

      <div
        className={clsx(styles.consensusStarRatingItem, {
          [styles.active]: selected,
        })}
      >
        <div className={styles.consensusLogoContainer}>
          <div
            className={clsx(styles.mobileServiceLogo, {
              [styles.active]: selected,
            })}
          >
            <On3LogoAlt className={styles.logo} fill="#a2a9ad" />
            <span className={styles.industryRankingText}>Industry Ranking</span>
          </div>
          <StarRating
            fiveStarPlus={fiveStarPlus}
            href={`/db/${slug}/industry-comparison/`}
            position={positionAbbr}
            small
            sport={sport?.name}
            stars={consensusStars}
            state={stateAbbr}
            year={`${year}`}
          />
          {consensusRating && (
            <Text
              className={clsx(styles.consensusRating, styles.desktop)}
              component="span"
            >
              {formattedConsensusRating}
            </Text>
          )}
        </div>

        <div className={clsx(styles.rankingHeaderContainer)}>
          <Text
            aria-label="Industry National Rank"
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Natl
          </Text>
          <Text
            aria-label="Industry Position Rank"
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Pos
          </Text>
          <Text
            aria-label="Industry State Rank"
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            St
          </Text>
        </div>

        <div className={styles.consensusRankingsContainer}>
          <Text className={styles.rank} variant="subtitle1">
            {consensusNationalRank ?? '-'}
          </Text>
          <Text className={styles.rank} variant="subtitle1">
            {consensusPositionRank ?? '-'}
          </Text>
          <Text className={styles.rank} variant="subtitle1">
            {consensusStateRank ?? '-'}
          </Text>
        </div>
      </div>
    </div>
  );
};
